import type { KeyboardEvent } from "react";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, TextField, Avatar, useTheme } from "@mui/material";
import { FoodsIconButton } from "@foodpilot/foods";
import { useUserContext } from "../../../../context/User/useUserContext";
import { useCreateComment } from "../../../../api/client-api/social/useComments";
import { useTranslation } from "react-i18next";

const MAX_COMMENT_LENGTH = 2000;

interface CompactCommentFormProps {
    postId: number;
    parentCommentId?: number;
    placeholder?: string;
    onCancel?: () => void;
    onSuccess?: () => void;
    autoFocus?: boolean;
}

export type CompactCommentFormHandle = {
    focus: () => void;
};

export const CompactCommentForm = forwardRef<CompactCommentFormHandle, CompactCommentFormProps>(
    ({ postId, parentCommentId, placeholder, onCancel, onSuccess, autoFocus = false }, ref) => {
        const theme = useTheme();
        const { user } = useUserContext();
        const [content, setContent] = useState("");
        const [isFocused, setIsFocused] = useState(false);
        const { t } = useTranslation();
        const resolvedPlaceholder = placeholder ?? t("social.comments.write_a_comment", "Write a comment...");
        const inputRef = useRef<HTMLInputElement>(null);
        const createComment = useCreateComment();

        useImperativeHandle(ref, () => ({
            focus: () => {
                setIsFocused(true);
                inputRef.current?.focus();
            },
        }));

        useEffect(() => {
            if (!autoFocus) {
                return;
            }

            setIsFocused(true);
            inputRef.current?.focus();
        }, [autoFocus]);

        const handleSubmit = async () => {
            if (!content.trim()) return;

            const commentData = {
                content: content.trim(),
                ...(parentCommentId && { parentComment: `/api/post_comments/${parentCommentId}` }),
            };

            try {
                await createComment.mutateAsync({ postId, data: commentData });
                setContent("");
                setIsFocused(false);
                onSuccess?.();
            } catch (error) {
                // Error handled by mutation
            }
        };

        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
            if (e.key === "Escape") {
                setContent("");
                setIsFocused(false);
                onCancel?.();
            }
        };

        const handleCancel = () => {
            setContent("");
            setIsFocused(false);
            onCancel?.();
        };

        if (!user) {
            return null;
        }

        const isSubmitting = createComment.isLoading;
        const isTooLong = content.length > MAX_COMMENT_LENGTH;
        const canSubmit = content.trim().length > 0 && !isTooLong && !isSubmitting;

        return (
            <Box display="flex" gap={1} alignItems="flex-start">
                <Avatar
                    src={(user as any).avatarUrl}
                    sx={{
                        width: 28,
                        height: 28,
                        fontSize: "0.75rem",
                    }}
                >
                    {user.firstname?.[0]}
                    {user.lastname?.[0]}
                </Avatar>

                <Box flex={1}>
                    <Box
                        sx={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: theme.custom.grey[100],
                            borderRadius: 3,
                            border: isFocused ? `2px solid ${theme.palette.primary[200]}` : "none",
                            "&:hover": {
                                backgroundColor: isFocused ? "white" : theme.palette.grey[200],
                            },
                        }}
                    >
                        <TextField
                            inputRef={inputRef}
                            fullWidth
                            multiline
                            maxRows={3}
                            placeholder={resolvedPlaceholder}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => !content.trim() && setIsFocused(false)}
                            onKeyDown={handleKeyPress}
                            disabled={isSubmitting}
                            variant="standard"
                            error={isTooLong}
                            helperText={
                                isTooLong ?
                                    t("social.comments.too_long", "Comments are limited to {{max}} characters.", {
                                        max: MAX_COMMENT_LENGTH,
                                    })
                                :   undefined
                            }
                            inputProps={{
                                maxLength: MAX_COMMENT_LENGTH,
                            }}
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    fontSize: "0.875rem",
                                    color: "text.primary",
                                    padding: "8px 12px",
                                    "& input": {
                                        color: "text.primary !important",
                                    },
                                    "& textarea": {
                                        color: "text.primary !important",
                                    },
                                },
                            }}
                            sx={{
                                "& .MuiInputBase-input": {
                                    color: "text.primary !important",
                                    fontSize: "0.875rem",
                                    "&::placeholder": {
                                        color: "text.secondary",
                                        opacity: 0.6,
                                    },
                                },
                            }}
                        />

                        {canSubmit && (
                            <FoodsIconButton
                                size="xs"
                                icon="sendMessage"
                                buttonProps={{
                                    onClick: handleSubmit,
                                    disabled: !canSubmit,
                                    sx: {
                                        color: theme.palette.primary.main,
                                        mr: 1,
                                    },
                                }}
                            />
                        )}
                    </Box>

                    {isFocused && content.trim() && (
                        <Box display="flex" justifyContent="flex-end" gap={1} mt={0.5}>
                            <Box
                                component="span"
                                sx={{
                                    fontSize: "0.75rem",
                                    color: "text.secondary",
                                }}
                            >
                                {t("social.comments.press_enter_to_post", "Press Enter to post")} •{" "}
                                {t("social.comments.escape_to_cancel", "Escape to cancel")} • {content.length}/
                                {MAX_COMMENT_LENGTH}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    },
);

CompactCommentForm.displayName = "CompactCommentForm";
